import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faMicrophone, faStar, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { searchVentures } from "../services/ventureApi";
const CHIPS = [
  "Kokapet",
  "Tukkuguda",
  "Nanakramguda",
  "Gandipet"
];
function Hero({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  suggestions,
  isSuggestionsLoading,
  suggestionsError
}) {
  const isControlled = typeof searchValue === "string";
  const [localSearchValue, setLocalSearchValue] = useState("");
  const [localSuggestions, setLocalSuggestions] = useState([]);
  const [localSuggestionsLoading, setLocalSuggestionsLoading] = useState(false);
  const [localSuggestionsError, setLocalSuggestionsError] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [voiceSearchMessage, setVoiceSearchMessage] = useState("");
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);

  const effectiveSearchValue = isControlled ? searchValue : localSearchValue;
  const effectiveSuggestions = Array.isArray(suggestions) ? suggestions : localSuggestions;
  const effectiveSuggestionsLoading = typeof isSuggestionsLoading === "boolean" ? isSuggestionsLoading : localSuggestionsLoading;
  const effectiveSuggestionsError = typeof suggestionsError === "string" ? suggestionsError : localSuggestionsError;

  const chipsRef = useRef(null);
  const recognitionRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => () => {
    recognitionRef.current?.abort();
  }, []);

  const syncArrowState = () => {
    const chipsElement = chipsRef.current;

    if (!chipsElement) {
      return;
    }

    const maxScrollLeft = chipsElement.scrollWidth - chipsElement.clientWidth;

    setShowLeftArrow(chipsElement.scrollLeft > 0);
    setShowRightArrow(maxScrollLeft > 0 && chipsElement.scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    syncArrowState();

    window.addEventListener("resize", syncArrowState);

    return () => {
      window.removeEventListener("resize", syncArrowState);
    };
  }, []);

  const handleChipsScroll = (direction) => {
    const chipsElement = chipsRef.current;

    if (!chipsElement) {
      return;
    }

    const scrollAmount = Math.max(chipsElement.clientWidth * 0.8, 220);

    chipsElement.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth"
    });
  };

  const updateSearchValue = (value) => {
    if (typeof onSearchChange === "function") {
      onSearchChange(value);
      return;
    }

    setLocalSearchValue(value);
  };

  const handleVoiceSearch = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceSearchMessage("Voice search is not supported by this browser. Try Chrome or Edge.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    setVoiceSearchMessage("Listening...");
    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim();
      if (transcript) {
        updateSearchValue(transcript);
        setVoiceSearchMessage(`Heard: ${transcript}`);
      }
    };

    recognition.onerror = (event) => {
      const messages = {
        "not-allowed": "Microphone access was denied. Allow microphone access and try again.",
        "service-not-allowed": "Voice search is blocked by browser settings.",
        "no-speech": "No speech detected. Try speaking again.",
        network: "Voice search is unavailable due to a network error."
      };
      setVoiceSearchMessage(messages[event.error] || "Voice search failed. Please try again.");
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setIsListening(false);
      setVoiceSearchMessage("Unable to start voice search. Please try again.");
    }
  };

  const handleSearch = async (value) => {
    setIsSuggestionsOpen(false);
    const normalized = value.trim();

    if (!normalized) {
      setLocalSuggestions([]);
      setLocalSuggestionsError("");
      return;
    }

    if (typeof onSearchSubmit === "function") {
      onSearchSubmit(value);
      return;
    }

    setLocalSuggestionsLoading(true);
    setLocalSuggestionsError("");

    try {
      const result = await searchVentures({
        query: normalized,
        pageNumber: 1,
        pageSize: 10
      });

      const nextSuggestions = result.items.map((item) => item?.ProjectName || item?.projectName || "").filter(Boolean);
      setLocalSuggestions(Array.from(new Set(nextSuggestions)).slice(0, 5));
    } catch (error) {
      setLocalSuggestions([]);
      setLocalSuggestionsError(error?.message || "Unable to search projects right now.");
    } finally {
      setLocalSuggestionsLoading(false);
    }
  };

  return <section className="vd-hero">
      <div className="vd-hero-inner">
        <h1 className="vd-hero-h1">
          Find the Right Real Estate Venture Using{" "}
          <span className="ai-word">
            AI
            <sup style={{ fontSize: "0.45em", verticalAlign: "super" }}>✦</sup>
          </span>
        </h1>
        <p className="vd-hero-sub">
          Search 10,000+ RERA Projects, Plots, Villas &amp; Apartments Across India
        </p>

        <form
          className="vd-searchbar"
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch(effectiveSearchValue);
          }}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} className="vd-searchbar-icon" />
          <input
    className="vd-searchbar-input"
    type="text"
    placeholder="Search Venture Area or Name"
    value={effectiveSearchValue}
    onChange={(event) => {
      updateSearchValue(event.target.value);
      setIsSuggestionsOpen(Boolean(event.target.value.trim()));
    }}
    aria-label="Search real estate ventures"
    aria-controls="venture-search-results"
    aria-expanded={isSuggestionsOpen && Boolean(effectiveSearchValue.trim())}
  />
          <span className="vd-search-divider" aria-hidden="true" />
          <button
            type="button"
            className={`vd-mic-btn${isListening ? " is-listening" : ""}`}
            title={isListening ? "Stop voice search" : "Voice search"}
            aria-label={isListening ? "Stop voice search" : "Start voice search"}
            aria-pressed={isListening}
            onClick={handleVoiceSearch}
          >
            <FontAwesomeIcon icon={faMicrophone} />
          </button>
          <button type="submit" className="vd-askai-btn">
            <FontAwesomeIcon icon={faStar} />
            Ask AI
          </button>
        </form>

        {voiceSearchMessage && <p className="vd-voice-search-message" role="status">{voiceSearchMessage}</p>}

        {isSuggestionsOpen && effectiveSearchValue.trim() && <div id="venture-search-results" className="vd-search-results" role="listbox" aria-label="Matching ventures">
            {effectiveSuggestionsLoading ? <p className="vd-search-results-message" role="status">Searching matching ventures...</p> :
              effectiveSuggestionsError ? <p className="vd-search-results-message is-error" role="alert">{effectiveSuggestionsError}</p> :
                effectiveSuggestions.length ? effectiveSuggestions.map((suggestion) => <button
                  key={suggestion}
                  type="button"
                  className="vd-search-result"
                  role="option"
                  onClick={() => handleSearch(suggestion)}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                  <span>{suggestion}</span>
                </button>) :
                  <p className="vd-search-results-message">No matching ventures found. Try a different project name, builder, or RERA number.</p>}
          </div>}

        <div className="vd-chips-wrap">
          <button className="vd-chip-arrow" aria-label="Scroll chip suggestions left" onClick={() => handleChipsScroll("left")} style={{
          visibility: showLeftArrow ? "visible" : "hidden",
          pointerEvents: showLeftArrow ? "auto" : "none"
        }}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="vd-chips" ref={chipsRef} onScroll={syncArrowState}>
            {CHIPS.map((chip) => <button key={chip} className="vd-chip" onClick={() => handleSearch(chip)}>
              {chip}
            </button>)}
          </div>
          <button className="vd-chip-arrow" aria-label="Scroll chip suggestions right" onClick={() => handleChipsScroll("right")} style={{
          visibility: showRightArrow ? "visible" : "hidden",
          pointerEvents: showRightArrow ? "auto" : "none"
        }}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </section>;
}
export {
  Hero as default
};
